import type { UseFormReturn } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import { CurrencyInput } from '@/shared/components/inputs';
import { DetailSectionHeader } from '@/shared/components/layout';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { formatCurrencyFromCents } from '@/shared/utils/money';
import type { ContractFormData } from '../types/contract-form';

type BaseProps = {
  id?: string;
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
};

export function ContractCreateBillingSection({ id, form }: BaseProps) {
  const { t, locale } = useIntl();

  return (
    <div id={id} className={id ? 'scroll-mt-28' : undefined}>
      <DetailSectionHeader title={t('contracts.billingSettings')} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
        <FormField
          control={form.control}
          name="billingTotalCents"
          render={({ field }) => (
            <FormItem className="md:col-span-1 h-full">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                <FormLabel>{t('contracts.billingTotal')}</FormLabel>
                <FormControl>
                  <CurrencyInput
                    testId="billing-total-input"
                    valueCents={Number(field.value || 0)}
                    onValueCentsChange={field.onChange}
                  />
                </FormControl>
                <div className="text-xs text-muted-foreground">
                  {formatCurrencyFromCents(Number(field.value || 0), locale)}
                </div>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billingInterval"
          render={({ field }) => (
            <FormItem className="md:col-span-1 h-full">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                <FormLabel>{t('contracts.billingInterval')}</FormLabel>
                <FormControl>
                  <Select value={field.value || 'monthly'} onValueChange={field.onChange}>
                    <SelectTrigger data-testid="billing-interval-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">
                        {t('contracts.billingIntervalOptions.monthly')}
                      </SelectItem>
                      <SelectItem value="quarterly">
                        {t('contracts.billingIntervalOptions.quarterly')}
                      </SelectItem>
                      <SelectItem value="yearly">
                        {t('contracts.billingIntervalOptions.yearly')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="paymentTermsDays"
          render={({ field }) => (
            <FormItem className="md:col-span-1 h-full">
              <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                <FormLabel>{t('contracts.paymentTermsDays')}</FormLabel>
                <FormControl>
                  <Input
                    data-testid="payment-terms-input"
                    type="number"
                    value={String(field.value ?? 30)}
                    onChange={(e) => field.onChange(Number.parseInt(e.target.value || '0', 10))}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
      </div>

      <div className="text-xs text-muted-foreground mt-2">{t('contracts.createBillingHint')}</div>

      <div className="mt-6">
        <div className="text-sm font-semibold text-foreground mb-3">
          {t('contracts.billingContact')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <FormField
            control={form.control}
            name="billingPoNumber"
            render={({ field }) => (
              <FormItem className="md:col-span-1 h-full">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                  <FormLabel>{t('contracts.billingPoNumber')}</FormLabel>
                  <FormControl>
                    <Input
                      data-testid="billing-po-input"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="billingContactName"
            render={({ field }) => (
              <FormItem className="md:col-span-1 h-full">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                  <FormLabel>{t('contracts.billingContactName')}</FormLabel>
                  <FormControl>
                    <Input
                      data-testid="billing-contact-name-input"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="billingContactEmail"
            render={({ field }) => (
              <FormItem className="md:col-span-1 h-full">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                  <FormLabel>{t('contracts.billingContactEmail')}</FormLabel>
                  <FormControl>
                    <Input
                      data-testid="billing-contact-email-input"
                      type="email"
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
